import React, { useState, useEffect, useCallback } from 'react';
import { Select, Field } from '@cloudsam/ui';
import SurveyExplorer from './components/SurveyExplorer';
import Toast from './components/Toast';
import {
    fetchSurveys,
    fetchEnrichedDeployments,
    fetchSurveyStructure,
} from './utils/explorerApi';
import { formatNLabel } from './utils/deploymentStats';
import { pickOpeningDeployment, pickOpeningSurvey } from './utils/openingSelection';
import { useContextLabel } from './contexts/IdleContext';

// ============================================================================
// App Shell
// Shows a survey/deployment selector in the header.
// URL params (deploymentId, surveyId) are used as initial selection if present.
// ============================================================================


function App() {
    const params = new URLSearchParams(window.location.search);
    const initialDeploymentId = params.get('deploymentId') || '';
    const initialSurveyId = params.get('surveyId') || '';
    const getSurveyId = (survey) => survey?.id || survey?.survey_id || '';

    // Survey/deployment selection state
    const [surveys, setSurveys] = useState([]);
    const [deployments, setDeployments] = useState([]);
    const [selectedSurveyId, setSelectedSurveyId] = useState(initialSurveyId);
    const [selectedDeploymentId, setSelectedDeploymentId] = useState(initialDeploymentId);
    const [selectionRule, setSelectionRule] = useState('latest');

    // Data state
    const [surveyStructure, setSurveyStructure] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ---- Load surveys on mount ----
    useEffect(() => {
        fetchSurveys()
            .then(data => {
                const list = Array.isArray(data) ? data : [];
                setSurveys(list);
                // Open on the newest survey rather than on an empty picker. An
                // author arriving at Explorer is almost always coming to look
                // at what they just ran, and an empty state asks them to
                // re-state something the data already knows. A URL parameter
                // is an explicit request and always wins.
                if (!initialSurveyId) setSelectedSurveyId(pickOpeningSurvey(list));
            })
            .catch(err => console.error('[Explorer] Failed to load surveys:', err))
            .finally(() => setLoading(false));
        // initialSurveyId is read once from the URL and never changes; it is
        // listed so the mount effect declares everything it reads.
    }, [initialSurveyId]);

    // Re-pull the enriched deployments for a survey. Returns the list (or null
    // on error). Kept selection-agnostic so it is safe to call repeatedly from
    // the live loop / Refresh without disturbing the current selection.
    const loadDeployments = useCallback(async (surveyId) => {
        if (!surveyId) {
            setDeployments([]);
            return [];
        }
        try {
            const data = await fetchEnrichedDeployments(surveyId);
            const list = Array.isArray(data) ? data : [];
            setDeployments(list);
            return list;
        } catch (err) {
            console.error('[Explorer] Failed to load deployments:', err);
            return null;
        }
    }, []);

    // ---- Load deployments when survey changes (also fixes selection) ----
    useEffect(() => {
        if (!selectedSurveyId) {
            setDeployments([]);
            return;
        }
        loadDeployments(selectedSurveyId).then(list => {
            if (!list) return;
            // Keep an explicit selection that still exists in this survey;
            // otherwise choose for the author. This covers both the first load
            // and a survey switch, where the previous deployment belongs to a
            // different survey and cannot simply be carried across.
            const stillSelectable = selectedDeploymentId && list.some(d => d.deployment_id === selectedDeploymentId);
            if (!stillSelectable) setSelectedDeploymentId(pickOpeningDeployment(list));
        });
    }, [selectedSurveyId, loadDeployments]); // eslint-disable-line react-hooks/exhaustive-deps

    // Live re-pull of the enriched deployments for the current survey. Wired to
    // both the Refresh button and the new-response signal in SurveyExplorer so
    // the counters / n / lifecycle labels stay live while a survey is Sent, and
    // flip to closed once it is undeployed.
    const refreshDeployments = useCallback(() => {
        if (selectedSurveyId) loadDeployments(selectedSurveyId);
    }, [selectedSurveyId, loadDeployments]);

    // ---- Load survey structure when survey changes ----
    useEffect(() => {
        if (!selectedSurveyId) return;
        fetchSurveyStructure(selectedSurveyId, selectedDeploymentId)
            .then(data => setSurveyStructure(data))
            .catch(() => setSurveyStructure(null));
    }, [selectedSurveyId, selectedDeploymentId]);

    // WP-D3b: re-pull the structure on demand (category adoption writes
    // response_categories into the descriptor server-side; the Analysis
    // surfaces need the fresh copy without a survey switch).
    // Returns the in-flight fetch so callers can await the fresh structure
    // before acting on it — a category edit re-runs the classifier against the
    // new list, and starting that against the old one re-derives the wrong
    // thing.
    const refreshStructure = useCallback(() => {
        if (!selectedSurveyId) return Promise.resolve();
        return fetchSurveyStructure(selectedSurveyId, selectedDeploymentId)
            .then(data => setSurveyStructure(data))
            .catch(() => { /* keep the current structure on transient failure */ });
    }, [selectedSurveyId, selectedDeploymentId]);

    // SurveyExplorer owns the response fetch and incremental polling — it
    // calls fetchExplorerResponses directly. App.js's job is selection state,
    // survey structure, and shell chrome.

    // ---- Handlers ----
    const handleSurveyChange = (e) => {
        const sid = e.target.value;
        setSelectedSurveyId(sid);
        setSelectedDeploymentId('');
        setError(null);
    };

    const handleDeploymentChange = (e) => {
        setSelectedDeploymentId(e.target.value);
        setError(null);
    };

    // ---- Derive display name ----
    const selectedSurvey = surveys.find(s => getSurveyId(s) === selectedSurveyId);
    const surveyName = selectedSurvey?.name || 'Survey Explorer';
    // Named for the lock screen. If this tab locks, its lock screen says which
    // survey it was browsing, so a person can tell what they'd be coming back
    // to. Nothing to save here — Explorer only browses what is already saved.
    useContextLabel(selectedSurvey ? surveyName : null);
    // Lifecycle state travels on every enriched-deployment row (surveys.status),
    // so read it off the current survey's deployments — same value for all rows.
    const surveyStatus = deployments[0]?.survey_status || '';
    /**
     * The Survey / Deployment selects for the Context panel (§6 item 4).
     * Field labels sit above the control; the native <select> is replaced by the
     * shared Select, which carries the design-system dropdown surface.
     */
    const renderDeploymentSelector = () => (
        <>
            <Field label="Survey" htmlFor="explorer-survey-select">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <Select
                        id="explorer-survey-select"
                        value={selectedSurveyId}
                        onChange={handleSurveyChange}
                        style={{ minWidth: 220 }}
                    >
                        <option value="">— Select a survey —</option>
                        {surveys.map(s => {
                            const optionSurveyId = getSurveyId(s);
                            return (
                                <option key={optionSurveyId} value={optionSurveyId}>{s.name}</option>
                            );
                        })}
                    </Select>
                    {selectedSurveyId && surveyStatus && (
                        <span style={styles.lifecycleBadge} title="Survey lifecycle state">
                            {surveyStatus}
                        </span>
                    )}
                </div>
            </Field>

            <Field label="Deployment" htmlFor="explorer-deployment-select">
                <Select
                    id="explorer-deployment-select"
                    value={selectedDeploymentId}
                    onChange={handleDeploymentChange}
                    disabled={!selectedSurveyId || deployments.length === 0}
                    style={{ minWidth: 320 }}
                >
                    <option value="">— Select a deployment —</option>
                    {deployments.map(d => (
                        <option key={d.deployment_id} value={d.deployment_id}>
                            {d.deploy_slug || d.deployment_id?.slice(0,8)} · {d.target_channel || '?'} · {d.language || 'en'} · n={formatNLabel(d)} · {d.is_open ? 'Open' : 'Closed'}
                        </option>
                    ))}
                </Select>
            </Field>
        </>
    );

    // ---- Render ----

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingCard}>
                    <p style={{ color: '#5F6368', fontFamily: styles.fontStack }}>Loading surveys...</p>
                </div>
            </div>
        );
    }

    // Always mount SurveyExplorer — it owns the full chrome (logo bar, command
    // bar, stats strip, tabs, body) and renders its own empty-state prompt
    // when no survey/deployment is selected. This keeps the context-free
    // startup visually consistent with the populated view, which matters for
    // new/trial accounts that have no deployments yet.
    return (
        <>
        <div style={styles.appContainer}>
            {/* Error display */}
            {error && (
                <div style={styles.errorBanner}>{error}</div>
            )}

            <SurveyExplorer
                surveyId={selectedSurveyId}
                deploymentId={selectedDeploymentId}
                surveyName={surveyName}
                surveyStructure={surveyStructure}
                onClose={() => window.close()}
                selectionRule={selectionRule}
                setSelectionRule={setSelectionRule}
                selectedDeployment={deployments.find(d => d.deployment_id === selectedDeploymentId)}
                onRefreshDeployments={refreshDeployments}
                onRefreshStructure={refreshStructure}
                deploymentSelector={renderDeploymentSelector()}
            />
        </div>
        <Toast />
        </>
    );
}

// ---- Styles — aligned to SAM Surveyor design system ----

const styles = {
    fontStack: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    appContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    },
    selectorBar: {
        padding: '10px 20px',
        background: '#1e3a5f',
        flexShrink: 0,
    },
    selectorControls: {
        display: 'flex',
        gap: 24,
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    selectorControlsLight: {
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    selectorLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: '#e0e7ef',
        fontSize: 13,
        fontWeight: 500,
    },
    selectorLabelLight: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: '#3C4043',
        fontSize: 13,
        fontWeight: 500,
    },
    select: {
        padding: '6px 12px',
        borderRadius: 4,
        border: '1px solid #4a6fa5',
        background: '#2a4f7a',
        color: '#ffffff',
        fontSize: 13,
        minWidth: 200,
        cursor: 'pointer',
        outline: 'none',
    },
    selectLight: {
        padding: '6px 12px',
        borderRadius: 4,
        border: '1px solid #DADCE0',
        background: '#ffffff',
        color: '#3C4043',
        fontSize: 13,
        minWidth: 200,
        cursor: 'pointer',
        outline: 'none',
    },
    lifecycleBadge: {
        marginLeft: 8,
        padding: '2px 8px',
        borderRadius: 10,
        background: '#EBF4FF',
        color: '#1967D2',
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'capitalize',
        letterSpacing: '0.3px',
    },
    container: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg-base)',
    },
    promptCard: {
        background: '#FFFFFF',
        borderRadius: 6,
        padding: 40,
        maxWidth: 500,
        border: '1px solid #E8EAED',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        textAlign: 'center',
    },
    loadingCard: {
        background: '#FFFFFF',
        borderRadius: 6,
        padding: 32,
        maxWidth: 400,
        margin: '80px auto',
        border: '1px solid #E8EAED',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        textAlign: 'center',
    },
    errorBanner: {
        backgroundColor: '#FFEBEE',
        color: '#F44336',
        padding: '8px 20px',
        fontSize: 13,
    },
};

export default App;
